<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUi } from '@/stores/ui.js'
import { unsavedEntry } from '@/composables/useUnsaved.js'
import UiButton from '@/components/ui/UiButton.vue'

/**
 * The bar under every page with unsaved work (see useUnsaved): it says so and saves it, so a
 * change at the top of a long form is saved without scrolling to its end — on a phone and on a
 * desktop alike. Ctrl+S (⌘S) saves too.
 */
const { t } = useI18n()
const ui = useUi()
const entry = unsavedEntry
const busy = ref(false)

const creating = computed(() => entry.value?.create())
const text = computed(() => {
  if (!entry.value) return ''
  if (creating.value) return t(entry.value.dirty() ? 'unsaved.newChanged' : 'unsaved.new')
  const n = entry.value.count()
  return n ? t('unsaved.fields', { n }) : t('unsaved.title')
})
// A phone has room for a few words: the same message, shortened.
const short = computed(() => {
  if (!entry.value) return ''
  if (creating.value) return t('unsaved.newShort')
  const n = entry.value.count()
  return n ? t('unsaved.fieldsShort', { n }) : t('unsaved.titleShort')
})

async function save() {
  const e = entry.value
  if (!e || busy.value || e.saving()) return
  busy.value = true
  try {
    const result = await e.save()
    if (result === null || result === false) {
      // Refused (a required field, a rule): show the first field that says why.
      await nextTick()
      document.querySelector('main .invalid, main [aria-invalid="true"], main [data-error]')?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  } finally {
    busy.value = false
  }
}
async function discard() {
  const e = entry.value
  if (!e?.discard) return
  if (e.dirty() && !(await ui.ask({ title: t('unsaved.discardTitle'), message: t('unsaved.discardMessage'), confirmLabel: t('unsaved.discard'), danger: true }))) return
  await e.discard()
}

function onKey(e) {
  if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's' || !entry.value) return
  // A dialog over the page saves itself.
  if (document.querySelector('[aria-modal="true"]')) return
  e.preventDefault()
  save()
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <Transition enter-active-class="transition duration-150" enter-from-class="translate-y-2 opacity-0" leave-active-class="transition duration-100" leave-to-class="opacity-0">
    <div v-if="entry" class="flex items-center gap-1.5 border-t border-amber-200 bg-amber-50 px-3 py-2 sm:px-6 dark:border-amber-900 dark:bg-amber-950/60" role="region" :aria-label="text" data-testid="unsaved-bar">
      <i :class="['fa-solid shrink-0 text-amber-600 dark:text-amber-400', creating ? 'fa-file-circle-plus' : 'fa-circle-exclamation']" aria-hidden="true"></i>
      <p class="min-w-0 flex-1 truncate text-sm font-medium" aria-live="polite"><span class="sm:hidden">{{ short }}</span><span class="hidden sm:inline">{{ text }}</span></p>
      <!-- what waits, shown in its own colour where the page can (D-170) -->
      <button v-if="entry.marks && !creating" type="button" :class="['inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm', entry.marks.on() ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200' : 'text-muted hover:bg-amber-100 dark:hover:bg-amber-900/40']" :aria-pressed="entry.marks.on()" data-testid="unsaved-marks" @click="entry.marks.toggle()">
        <i class="fa-solid fa-highlighter" aria-hidden="true"></i><span class="hidden sm:inline">{{ $t('unsaved.showMarks') }}</span>
      </button>
      <span class="hidden text-xs text-faint md:inline">{{ $t('unsaved.keys') }}</span>
      <UiButton v-if="entry.discard" size="sm" variant="ghost" :icon="creating ? 'fa-xmark' : 'fa-rotate-left'" :title="creating ? $t('common.cancel') : $t('unsaved.discard')" :aria-label="creating ? $t('common.cancel') : $t('unsaved.discard')" data-testid="unsaved-discard" @click="discard"><span class="hidden sm:inline">{{ creating ? $t('common.cancel') : $t('unsaved.discard') }}</span></UiButton>
      <UiButton size="sm" variant="primary" icon="fa-check" :loading="busy || entry.saving()" data-testid="unsaved-save" @click="save">{{ creating ? $t('common.create') : $t('common.save') }}</UiButton>
    </div>
  </Transition>
</template>
